// Billing settings — manage the receiving (banking) accounts printed on this
// tenant's proforma invoices. Multi-tenant self-service: no code change needed
// to add Wise / Amex / a new bank. Owner/admin only on the server (PUT is
// role-gated); a non-privileged user gets a 403 on save.

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Star, Building2 } from 'lucide-react';
import { AppShell } from '@/components/app-shell';
import { ShipFromLocationsSection } from '@/components/ship-from-locations';
import {
  api,
  type CardPaymentSettingsResponse,
  type ListPaymentAccountsResponse,
  type PaymentAccountDto,
} from '@/lib/api';

type Draft = PaymentAccountDto;

const BLANK: Omit<Draft, 'id'> = {
  label: '',
  hint: '',
  isDefault: false,
  beneficiaryName: '',
  beneficiaryAddress: '',
  bankName: '',
  bankAddress: '',
  swiftBic: '',
  abaRouting: '',
  accountNumber: '',
  intermediaryBic: '',
  feeTerms:
    'Sender pays all wire/FX fees (SHA/OUR). Beneficiary must receive the invoiced amount net of all fees.',
};

// Fields shown in the edit form, in order. Label + which are required.
const FIELDS: Array<{ key: keyof Omit<Draft, 'id' | 'isDefault'>; label: string; required?: boolean; wide?: boolean }> = [
  { key: 'label', label: 'Account label (shown in the dropdown)', required: true },
  { key: 'hint', label: 'Hint (e.g. “International customers”)' },
  { key: 'beneficiaryName', label: 'Beneficiary name', required: true },
  { key: 'beneficiaryAddress', label: 'Beneficiary address', wide: true },
  { key: 'bankName', label: 'Bank name', required: true },
  { key: 'bankAddress', label: 'Bank address', wide: true },
  { key: 'swiftBic', label: 'SWIFT / BIC' },
  { key: 'abaRouting', label: 'ABA / routing' },
  { key: 'accountNumber', label: 'Account number', required: true },
  { key: 'intermediaryBic', label: 'Intermediary bank (optional)', wide: true },
  { key: 'feeTerms', label: 'Fee terms', wide: true },
];

export function BillingPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['payment-accounts'],
    queryFn: () =>
      api<ListPaymentAccountsResponse>('/api/billing/payment-accounts', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 30_000,
  });

  const [accounts, setAccounts] = useState<Draft[]>([]);
  const [dirty, setDirty] = useState(false);

  // Load server state into the editable list once (and after a save resets dirty).
  useEffect(() => {
    if (data && !dirty) setAccounts(data.accounts);
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api<ListPaymentAccountsResponse>('/api/billing/payment-accounts', {
        method: 'PUT',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ accounts }),
      }),
    onSuccess: (res) => {
      setAccounts(res.accounts);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['payment-accounts'] });
    },
  });

  function update(idx: number, patch: Partial<Draft>) {
    setDirty(true);
    setAccounts((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function addAccount() {
    setDirty(true);
    setAccounts((prev) => [
      ...prev,
      { ...BLANK, id: `new_${prev.length}_${Date.now()}`, isDefault: prev.length === 0 },
    ]);
  }
  function removeAccount(idx: number) {
    setDirty(true);
    setAccounts((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Keep a default if we removed the current one.
      if (next.length > 0 && !next.some((a) => a.isDefault)) next[0]!.isDefault = true;
      return [...next];
    });
  }
  function makeDefault(idx: number) {
    setDirty(true);
    setAccounts((prev) => prev.map((a, i) => ({ ...a, isDefault: i === idx })));
  }

  // Basic validity: required fields present. Mirrors the server's zod min(1)s so
  // the broker isn't surprised by a 400.
  const invalid = accounts.some(
    (a) => !a.label.trim() || !a.beneficiaryName.trim() || !a.bankName.trim() || !a.accountNumber.trim(),
  );

  const saveErr = save.isError ? (save.error as { message?: string })?.message ?? 'Save failed' : null;

  const headerActions = (
    <>
      <button onClick={addAccount} className="btn-ghost text-xs" aria-label="Add account">
        <Plus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Add account</span>
      </button>
      <button
        onClick={() => save.mutate()}
        disabled={!dirty || invalid || save.isPending}
        className="btn-primary text-xs disabled:opacity-50"
      >
        {save.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </>
  );

  return (
    <AppShell
      title="Billing & settings"
      subtitle="Receiving accounts, card payments, ship-from locations and document signatures."
      headerActions={headerActions}
    >
      <div className="h-full overflow-auto px-4 sm:px-6 py-6">
        <div className="max-w-3xl mx-auto space-y-5">
        {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {isError && (
          <div className="text-sm text-destructive">
            {(error as { message?: string })?.message ?? 'Failed to load billing settings'}
          </div>
        )}

        {saveErr && (
          <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded p-2">
            {saveErr}
          </div>
        )}

        {!isLoading && accounts.length === 0 && (
          <div className="text-sm text-muted-foreground border border-dashed border-border rounded p-6 text-center">
            No receiving accounts yet. Click <span className="font-medium">Add account</span> to create
            one. Proformas issued before you add an account show a “payment details provided
            separately” note.
          </div>
        )}

        {accounts.map((acct, idx) => (
          <div key={acct.id} className="border border-border rounded-lg bg-surface">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden />
                <span className="font-medium text-sm truncate">{acct.label || 'Untitled account'}</span>
                {acct.isDefault && (
                  <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[11px] font-medium">
                    Default
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {!acct.isDefault && (
                  <button
                    onClick={() => makeDefault(idx)}
                    className="text-xs px-2 py-0.5 border border-border rounded hover:bg-muted inline-flex items-center gap-1"
                    title="Use as the default receiving account"
                  >
                    <Star className="w-3 h-3" /> Make default
                  </button>
                )}
                <button
                  onClick={() => removeAccount(idx)}
                  className="text-xs px-2 py-0.5 border border-border rounded text-destructive hover:bg-destructive/10 inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <label key={f.key} className={`block ${f.wide ? 'sm:col-span-2' : ''}`}>
                  <span className="block text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                    {f.label}
                    {f.required && <span className="text-destructive"> *</span>}
                  </span>
                  <input
                    type="text"
                    value={acct[f.key]}
                    onChange={(e) => update(idx, { [f.key]: e.target.value } as Partial<Draft>)}
                    className="w-full px-2 py-1.5 border border-border rounded text-sm bg-background"
                  />
                </label>
              ))}
            </div>
          </div>
        ))}

        {accounts.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Bank details are stored per tenant and only ever appear on your own proformas. One account
            is always the default; it's used when a proforma doesn't specify one.
          </p>
        )}

        <CardPaymentsSection />
        <ShipFromLocationsSection />
        <DocumentSignatureSection />
      </div>
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Card payments (Stripe) — per-tenant switch for proforma card-payment links.
// Phase 1: charges settle to the platform Stripe account, so this stays OFF
// unless this workspace's receivables are the platform's. The fee is passed to
// the customer as a visible line, grossed up so the invoice nets in full.
// ---------------------------------------------------------------------------
function CardPaymentsSection() {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['card-payment-settings'],
    queryFn: () =>
      api<CardPaymentSettingsResponse>('/api/billing/card-payment-settings', {
        tokenGetter: () => getToken(),
      }),
    staleTime: 30_000,
  });

  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      api<CardPaymentSettingsResponse>('/api/billing/card-payment-settings', {
        method: 'PUT',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          enabled,
          feePctBps: data?.settings.feePctBps ?? 290,
          feeFixedCents: data?.settings.feeFixedCents ?? 30,
        }),
      }),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['card-payment-settings'] });
    },
    onError: (e) => setErr((e as Error)?.message ?? 'Save failed'),
  });

  if (isLoading || !data) return null;
  const { settings, stripeConfigured } = data;
  const feePct = (settings.feePctBps / 100).toFixed(settings.feePctBps % 100 === 0 ? 0 : 1);
  const feeFixed = (settings.feeFixedCents / 100).toFixed(2);

  return (
    <div className="border border-border rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold">Card payments (Stripe)</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Lets you send customers a secure card-payment link on a proforma (typically urgent / AOG
            orders). A {feePct}% + ${feeFixed} processing fee is added as its own line, grossed up so
            you net the full invoice amount.
          </p>
        </div>
        <button
          onClick={() => save.mutate(!settings.enabled)}
          disabled={save.isPending || (!settings.enabled && !stripeConfigured)}
          className={`text-xs px-3 py-1.5 rounded-md border ${
            settings.enabled
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'border-border hover:bg-muted'
          } disabled:opacity-50`}
        >
          {save.isPending ? 'Saving…' : settings.enabled ? 'Enabled — click to disable' : 'Enable card payments'}
        </button>
      </div>
      {!stripeConfigured && (
        <p className="text-[11px] text-amber-700">
          Stripe keys are not configured on the server yet (STRIPE_SECRET_KEY / STRIPE_WEBHOOK_SECRET) —
          card links can't be created until they are.
        </p>
      )}
      {err && <p className="text-[11px] text-destructive">{err}</p>}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Document signature (Phase 1B)
//
// Fixes an operator-reported bug: every quote, proforma, vendor PO and
// commercial invoice Miranda sent was signed "Austin Stout — Founder &
// Managing Member". The signer lived only in the tenant-level seller profile,
// which is NULL for APD, so all four document types fell through to the
// hardcoded DEFAULT_SELLER regardless of who clicked Generate.
//
// Editing model: anyone edits their own; owner/admin edit anyone's. Every
// change is audited server-side — this title prints on commercial invoices,
// which are customs documents.
// ---------------------------------------------------------------------------

interface TeamMemberDto {
  membershipId: string;
  role: string;
  email: string;
  fullName: string | null;
  representativeName: string | null;
  representativeTitle: string | null;
  isSelf: boolean;
}

function DocumentSignatureSection() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, { name: string; title: string }>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['team-members'],
    queryFn: () =>
      api<{ members: TeamMemberDto[] }>('/api/team/members', { tokenGetter: () => getToken() }),
    staleTime: 30_000,
  });

  const save = useMutation({
    mutationFn: (m: TeamMemberDto) =>
      api<{ member: TeamMemberDto }>(`/api/team/members/${m.membershipId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({
          representativeName: drafts[m.membershipId]?.name ?? m.representativeName ?? '',
          representativeTitle: drafts[m.membershipId]?.title ?? m.representativeTitle ?? '',
        }),
      }),
    onSuccess: (_res, m) => {
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[m.membershipId];
        return next;
      });
      qc.invalidateQueries({ queryKey: ['team-members'] });
    },
  });

  const members = data?.members ?? [];
  // The server allows self-edit, or any edit by owner/admin. Mirror that rule
  // here so an agent never gets a live-looking input that 403s on save.
  const myRole = members.find((m) => m.isSelf)?.role;
  const canEditAnyone = myRole === 'owner' || myRole === 'admin';

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <Building2 className="h-5 w-5 text-slate-500" />
        <h2 className="text-lg font-semibold text-slate-900">Document signature</h2>
      </div>
      <p className="mb-4 max-w-2xl text-sm leading-relaxed text-slate-600">
        Printed as the representative on quotes, proforma invoices, vendor POs and
        commercial invoices. Each person signs documents they generate. Leave blank to
        fall back to the company default. Changes are recorded in the audit log.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="grid gap-3">
          {members.map((m) => {
            const draft = drafts[m.membershipId];
            const name = draft?.name ?? m.representativeName ?? '';
            const title = draft?.title ?? m.representativeTitle ?? '';
            const dirty = Boolean(draft);
            const canEdit = m.isSelf || canEditAnyone;
            return (
              <div key={m.membershipId} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{m.fullName || m.email}</span>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                    {m.role}
                  </span>
                  {m.isSelf ? (
                    <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">you</span>
                  ) : null}
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-xs font-medium text-slate-600">Name on documents</span>
                    <input
                      className="min-h-[2.5rem] rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                      value={name}
                      disabled={!canEdit}
                      placeholder={m.fullName ?? 'e.g. Miranda Stout'}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [m.membershipId]: { name: e.target.value, title },
                        }))
                      }
                    />
                  </label>
                  <label className="grid gap-1">
                    <span className="text-xs font-medium text-slate-600">Job title</span>
                    <input
                      className="min-h-[2.5rem] rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                      value={title}
                      disabled={!canEdit}
                      placeholder="e.g. Director of Sales"
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [m.membershipId]: { name, title: e.target.value },
                        }))
                      }
                    />
                  </label>
                </div>
                {save.isError && save.variables?.membershipId === m.membershipId ? (
                  <p className="mt-3 text-sm text-red-600">
                    {(save.error as Error)?.message || 'Could not save. Try again.'}
                  </p>
                ) : null}
                {dirty && canEdit ? (
                  <button
                    type="button"
                    onClick={() => save.mutate(m)}
                    disabled={save.isPending}
                    className="mt-3 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                  >
                    {save.isPending ? 'Saving…' : 'Save signature'}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
